import { TicketTypeEnum } from 'api-hooks/booking/model';
import { useGetBookingTicket } from 'api-hooks/booking/query';
import { wjlTimeConversion } from 'common/utils/date';
import { string2money } from 'common/utils/string';
import DialogBackdrop from 'components/commons/dialog/dialog-backdrop';
import IconBox from 'components/commons/icon-box';
import Stack from 'components/commons/stack';
import Text from 'components/commons/text';
import FetchWrapperComponent from 'components/elements/fetch-wrapper-component';
import staticFilePaths from 'constant/static-file';
import { format } from 'date-fns';
import Image from 'next/image';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import QRious from 'qrious';
import * as React from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, X } from 'react-feather';
import { useSwipeable } from 'react-swipeable';
import typography from 'styles/typography';

import DetailFee from './detail-fee';
import {
  BlueText,
  Circle,
  Container,
  ContainerIndicator,
  Indicator,
  LeftContent,
  LocationText,
  PassengerInformationText,
  PortText,
  ReminderText,
  RightContent,
  SmallText,
  styles,
  TicketContainer,
  TicketContent,
  TicketLogoContainer,
  TicketLogoImage,
  ticketStyles,
  TimeText,
} from './style';

interface Props {
  onClose: () => void;
  bookingId: string;
  cruiseName: string;
}

export default function Ticket(props: Props) {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const [state, setState] = React.useState(0);
  const { data, isLoading, error, isFetching } = useGetBookingTicket({
    bookingId: props.bookingId,
  });

  const onPrev = React.useCallback(() => {
    if (data && data.data) {
      setState((prev) => {
        if (prev === 0) {
          return data.data.length - 1;
        }
        return prev - 1;
      });
    }
  }, [data]);

  const onNext = React.useCallback(() => {
    if (data && data.data) {
      setState((prev) => {
        if (prev === data.data.length - 1) {
          return 0;
        }
        return prev + 1;
      });
    }
  }, [data]);

  const swipeHandler = useSwipeable({
    onSwipedLeft: onNext,
    onSwipedRight: onPrev,
  });

  const tickets = (data?.data || []).map((ticket) => {
    const isPassenger = ticket.type === TicketTypeEnum.PASSENGER;
    let ticketTitle = t(`booking:${ticket.type.toLowerCase()}_ticket`);
    if (ticket.isFacilitated) {
      ticketTitle = t('booking:vehicle_passenger_ticket');
    }

    let dayCycle = ticket.departureAt.dayCycle;
    let departureAt = wjlTimeConversion(ticket.departureAt);
    let departureTime = ticket.departureAt.time!;
    if (ticket.delayedDepartureAt.date) {
      departureAt = wjlTimeConversion(ticket.delayedDepartureAt);
    }

    if (ticket.delayedDepartureAt.time) {
      departureTime = ticket.delayedDepartureAt.time;
      dayCycle = ticket.delayedDepartureAt.dayCycle;
    }

    let informationTitle = t('booking:vehicle_detail');
    let informationContent = '-';
    if (isPassenger) {
      informationTitle = t('common:class');
      if (ticket.seatNumber) {
        informationContent = `${t('booking:economy')} - ${ticket.seatNumber}`;
      } else {
        informationContent = ticket.facility || '-';
      }
    } else {
      if (ticket.driver) {
        informationTitle = t('common:driver_name');
        informationContent = ticket.driver;
      } else {
        informationContent = ticket.vehicleDescription || '-';
      }
    }

    const facilityPrice =
      string2money(ticket.priceSummary.facilityPrice) || '-';
    const total = ticket.priceSummary.total;

    return {
      ...ticket,
      ticketTitle,
      departureAt,
      transportaionPrice: string2money(ticket.priceSummary.price) || '-',
      insurancePrice: string2money(ticket.priceSummary.insurancePrice) || '-',
      adminFee: string2money(ticket.priceSummary.adminUser) || '-',
      total: string2money(total) || '-',
      departureTime,
      dayCycle,
      informationTitle,
      informationContent,
      facilityPrice,
    };
  });

  const ticket = tickets[state];

  const qrCodeRef = React.useRef(null);

  React.useEffect(() => {
    if (!ticket && !qrCodeRef.current) return;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const qr = new QRious({
      element: qrCodeRef.current,
      value: ticket.code,
    });
  }, [ticket]);

  const priceComponent = ticket && (
    <>
      <DetailFee
        label={t('common:transportation')}
        value={ticket.transportaionPrice}
      />
      <DetailFee label={t('common:facility')} value={ticket.facilityPrice} />
      <DetailFee label={t('common:insurance')} value={ticket.insurancePrice} />
      <DetailFee label={t('common:admin_fee')} value={ticket.adminFee} />
      <DetailFee label={t('common:total')} value={ticket.total} />
    </>
  );

  return (
    <DialogBackdrop
      onClose={props.onClose}
      cancelable
      css={{
        background: 'rgba(0, 0, 0, 0.25)',
        padding: 4,
        '& > .react-reveal': {
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          height: '100%',
          overflow: 'auto',
          '@lg': {
            alignItems: 'center',
          },
          '& > div': {
            width: '100%',
            '@md': {
              maxWidth: 1000,
            },
          },
        },
      }}
    >
      <Container>
        <Stack.Item alignItems="end" css={{ mb: 8 }}>
          <IconBox css={ticketStyles.closeContainer} onClick={props.onClose}>
            <X />
          </IconBox>
        </Stack.Item>
        <FetchWrapperComponent
          error={error}
          isLoading={isLoading || isFetching}
          loadingComponent={<></>}
          component={
            <>
              {ticket && (
                <TicketContainer>
                  <IconBox css={styles.leftChevron} onClick={onPrev}>
                    <ChevronLeft />
                  </IconBox>
                  <TicketContent {...swipeHandler}>
                    <LeftContent>
                      <Stack>
                        <Stack.Item
                          direction="horizontal"
                          alignItems="center"
                          css={{
                            borderBottom: '2px dashed $neutral2',
                            pb: 20,
                            position: 'relative',
                            '@lg': {
                              borderBottom: 'none',
                              padding: 0,
                              position: 'static',
                            },
                          }}
                        >
                          <Stack.Item
                            css={{
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              width: '100%',
                              '@lg': {
                                display: 'flex',
                                height: '5rem',
                                flexDirection: 'row',
                                borderBottom: '2px solid $dbGray1',
                              },
                            }}
                          >
                            <TicketLogoContainer>
                              <TicketLogoImage
                                src={staticFilePaths.WJLLogoDefaultSVG}
                                alt="logo-ticket"
                              />
                            </TicketLogoContainer>
                            <Text
                              css={{
                                textAlign: 'center',
                                ...typography.body1,
                                fontWeight: 600,
                                '@md': typography.headline4,
                                '@lg': {
                                  display: 'flex',
                                  mx: 20,
                                  textAlign: 'start',
                                },
                              }}
                            >
                              {t('common:cross_ticket')}
                            </Text>
                          </Stack.Item>
                          <Circle
                            css={{
                              left: -25,
                              bottom: -10,
                              width: 20,
                              height: 20,
                              '@lg': { display: 'none' },
                            }}
                          />
                          <Circle
                            css={{
                              right: -25,
                              bottom: -10,
                              width: 20,
                              height: 20,
                              '@lg': {
                                display: 'none',
                              },
                            }}
                          />
                        </Stack.Item>
                        <Text
                          variant="headline7"
                          css={{
                            fontWeight: 700,
                            mt: 16,
                            '@md': {
                              ...typography.headline5,
                              fontWeight: 700,
                            },
                            '@lg': {
                              display: 'none',
                            },
                          }}
                          align="center"
                        >
                          {ticket.ticketTitle}
                        </Text>

                        <Stack
                          rootCss={{
                            mt: 8,
                            mb: 16,
                            '@lg': {
                              flexDirection: 'row',
                              mt: 16,
                              mb: 16,
                            },
                          }}
                          itemCss={{
                            flex: 1,
                            alignItems: 'center',
                            '@lg': { alignItems: 'start' },
                          }}
                          gap="xs"
                        >
                          <TimeText>
                            {`${format(ticket.departureAt, 'dd MMM yyyy | ')} ${
                              ticket.departureTime
                            } ${ticket.dayCycle}`}
                          </TimeText>
                          <TimeText css={{ '@lg': { alignSelf: 'end' } }}>
                            {props.cruiseName}
                          </TimeText>
                        </Stack>

                        <Stack.Item>
                          <Stack direction="horizontal">
                            <Stack.Item>
                              <SmallText>{t('common:from')}</SmallText>
                              <PortText>{ticket.fromHarbor}</PortText>
                              <LocationText>{ticket.fromCity}</LocationText>
                            </Stack.Item>
                            <Stack.Item css={{ mx: 16 }}>
                              <ArrowRight />
                            </Stack.Item>
                            <Stack.Item>
                              <SmallText>{t('common:to')}</SmallText>
                              <PortText>{ticket.toHarbor}</PortText>
                              <LocationText>{ticket.toCity}</LocationText>
                            </Stack.Item>
                          </Stack>
                        </Stack.Item>
                      </Stack>
                      <Stack direction="horizontal" rootCss={{ mt: 16 }}>
                        <Stack.Item
                          css={{
                            backgroundColor: '$neutral1',
                            px: 20,
                            py: 12,
                            flex: 1,
                          }}
                        >
                          <Text variant="body3" css={{ mb: 8 }}>
                            {ticket.informationTitle}
                          </Text>
                          <BlueText>{ticket.informationContent}</BlueText>
                        </Stack.Item>
                      </Stack>
                      <Stack
                        direction="horizontal"
                        rootCss={{
                          mt: 16,
                          backgroundColor: '$neutral1',
                          px: 8,
                          py: 12,
                          display: 'none',
                          '@md': {
                            px: 20,
                          },
                          '@lg': {
                            display: 'flex',
                          },
                        }}
                      >
                        <Stack.Item css={{ flex: 2, pr: 8 }} justify="between">
                          {priceComponent}
                        </Stack.Item>
                        {ticket.isFacilitated && (
                          <Stack.Item css={{ flex: 1, alignSelf: 'center' }}>
                            <Image
                              src={
                                locale === 'en'
                                  ? staticFilePaths.facilitatedEnWEBP
                                  : staticFilePaths.facilitatedIdWEBP
                              }
                              alt="facilitated-by-vehicle"
                              width={168}
                              height={102}
                              layout="responsive"
                            />
                          </Stack.Item>
                        )}
                      </Stack>
                    </LeftContent>
                    <RightContent>
                      <Circle
                        css={{
                          top: -20,
                          left: -20,
                          display: 'none',
                          '@lg': { display: 'flex' },
                        }}
                      />
                      <Circle
                        css={{
                          top: -20,
                          right: -20,
                          display: 'none',
                          '@lg': {
                            bottom: -20,
                            left: -20,
                            top: 'initial',
                            right: 'initial',
                            display: 'flex',
                          },
                        }}
                      />

                      <Text
                        variant="headline4"
                        css={{
                          display: 'none',
                          '@lg': {
                            height: '5rem',
                            display: 'flex',
                            alignItems: 'center',
                            maxHeight: '5rem',
                            borderBottom: '2px solid $dbGray1',
                          },
                        }}
                      >
                        {ticket.ticketTitle}
                      </Text>
                      <Stack.Item css={{ flex: 1 }} justify="around">
                        <Stack
                          direction="horizontal"
                          alignItems="center"
                          rootCss={{
                            py: 32,
                            borderTop: '2px solid rgba(157, 166, 174, 0.4)',
                            borderBottom: '2px solid rgba(157, 166, 174, 0.4)',
                            '@lg': { border: 'none' },
                          }}
                          gap="xs"
                        >
                          <Stack.Item grow>
                            {ticket.type === 'Passenger' && (
                              <>
                                <Stack.Item
                                  css={{
                                    maxWidth: 100,
                                    '@sm': { maxWidth: 'fit-content' },
                                  }}
                                >
                                  <Text variant="body5">
                                    {t('common:passenger_name')}
                                  </Text>
                                  <PassengerInformationText>
                                    {ticket.name}
                                  </PassengerInformationText>
                                </Stack.Item>
                                <Stack.Item
                                  css={{
                                    mt: 12,
                                    maxWidth: 100,
                                    '@sm': { maxWidth: 'fit-content' },
                                  }}
                                >
                                  <Text variant="body5">{t('common:age')}</Text>
                                  <PassengerInformationText>
                                    {t('common:age_extra', {
                                      extra: ticket.age,
                                    })}
                                  </PassengerInformationText>
                                </Stack.Item>
                                <Stack.Item
                                  css={{
                                    mt: 12,
                                    maxWidth: 100,
                                    '@sm': { maxWidth: 'fit-content' },
                                  }}
                                >
                                  <Text variant="body5">
                                    {t('common:identity_number')}
                                  </Text>
                                  <PassengerInformationText>
                                    {ticket.identifierCode || '-'}
                                  </PassengerInformationText>
                                </Stack.Item>
                                {ticket.vehicleNumber && (
                                  <Stack.Item
                                    css={{
                                      mt: 12,
                                      maxWidth: 'fit-content',
                                    }}
                                  >
                                    <Text variant="body5">
                                      {t('common:vehicle_registration_number')}
                                    </Text>
                                    <PassengerInformationText>
                                      {ticket.vehicleNumber}
                                    </PassengerInformationText>
                                  </Stack.Item>
                                )}
                              </>
                            )}

                            {ticket.type === 'Vehicle' && (
                              <>
                                <Stack.Item>
                                  <Text variant="body5">
                                    {t('common:vehicle_registration_number')}
                                  </Text>
                                  <PassengerInformationText>
                                    {ticket.identifierCode}
                                  </PassengerInformationText>
                                </Stack.Item>
                                <Stack.Item css={{ mt: 12 }}>
                                  <Text variant="body5">
                                    {t('common:vehicle_type')}
                                  </Text>
                                  <PassengerInformationText>
                                    {ticket.vehicleType}
                                  </PassengerInformationText>
                                </Stack.Item>
                                {typeof ticket?.vehicleMaxHeightMeter ===
                                  'string' && (
                                  <Stack.Item css={{ mt: 12 }}>
                                    <Text variant="body5">
                                      {t('common:max_height')}
                                    </Text>
                                    <PassengerInformationText>
                                      {ticket.vehicleMaxHeightMeter} Meter
                                    </PassengerInformationText>
                                  </Stack.Item>
                                )}
                              </>
                            )}
                          </Stack.Item>
                          <Stack.Item alignItems="center">
                            {data && (
                              <>
                                <canvas ref={qrCodeRef} />
                                <Text variant="body5" css={{ mt: 8 }}>
                                  {ticket.code}
                                </Text>
                                <Text variant="body3Semibold">
                                  {props.cruiseName}
                                </Text>
                              </>
                            )}
                          </Stack.Item>
                        </Stack>
                        <Stack
                          direction="horizontal"
                          rootCss={{
                            mt: 16,
                            backgroundColor: '$neutral1',
                            px: 8,
                            py: 12,
                            display: 'flex',
                            '@md': {
                              px: 20,
                            },
                            '@lg': { display: 'none' },
                          }}
                        >
                          <Stack.Item
                            css={{ flex: 2, pr: 8 }}
                            justify="between"
                          >
                            {priceComponent}
                          </Stack.Item>
                          {ticket.isFacilitated && (
                            <Stack.Item css={{ flex: 1, alignSelf: 'center' }}>
                              <Image
                                src={
                                  locale === 'en'
                                    ? staticFilePaths.facilitatedEnWEBP
                                    : staticFilePaths.facilitatedIdWEBP
                                }
                                alt="facilitated-by-vehicle"
                                width={168}
                                height={102}
                                layout="responsive"
                              />
                            </Stack.Item>
                          )}
                        </Stack>
                        <Stack.Item css={{ mt: 8 }}>
                          <ReminderText css={{ fontWeight: 600 }}>
                            {t('common:reminder')}
                          </ReminderText>
                          <ul style={{ marginTop: 4, paddingInlineStart: 28 }}>
                            <li>
                              <ReminderText>
                                {ticket.type === 'Vehicle'
                                  ? t('booking:policy_01_vehicle')
                                  : t('booking:policy_01')}
                              </ReminderText>
                            </li>
                            <li>
                              <ReminderText>
                                {t('booking:policy_02')}
                              </ReminderText>
                            </li>
                            <li>
                              <ReminderText>
                                {t('booking:policy_03')}
                              </ReminderText>
                            </li>
                            <li>
                              <ReminderText>
                                {t('booking:policy_04')}
                              </ReminderText>
                            </li>
                          </ul>
                        </Stack.Item>
                      </Stack.Item>
                    </RightContent>
                  </TicketContent>
                  <IconBox css={styles.rightChevron} onClick={onNext}>
                    <ChevronRight />
                  </IconBox>
                </TicketContainer>
              )}
            </>
          }
        />
      </Container>
      <ContainerIndicator>
        {(data?.data || []).map((value, index) => (
          <Indicator
            key={index}
            onClick={() => setState(index)}
            variant={state === index ? 'active' : 'inactive'}
          />
        ))}
      </ContainerIndicator>
    </DialogBackdrop>
  );
}
